import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion, Divider, ITabItem, SummaryInformationCategory, Tabs, TAccordionItem } from '@treezor/design-system';

import { useCreateBusinessUser } from '@/features/user/hooks/createUser/useCreateBusinessUser';
import { AddressSummary } from '@/features/user/components/CreateUser/AddressSummary';
import { TaxResidenceSummary } from '@/features/user/components/CreateUser/TaxResidenceSummary';
import { KycDocumentSummary } from '@/features/user/components/CreateUser/KycDocumentSummary';
import { ICreateUserInfo } from '@/features/user/contexts/CreateUserContext';
import { PersonalSituationSummary } from '@/features/user/components/CreateUser/PersonalSituationSummary';

export function CompanyMembersSummary() {
  const { user, config } = useCreateBusinessUser();

  const { t } = useTranslation();

  const [currentTabIndexes, setCurrentTabIndexes] = useState<number[]>(user?.members?.map((_) => 0));

  const tabItems = useCallback(
    (member: ICreateUserInfo, memberIndex) => {
      return [
        {
          label: t('pages.dashboard.createUser.form.pages.userInformation'),
          element: (
            <div className="flex flex-col gap-5" key={`user-info-tab-${memberIndex}`}>
              <p className="text-secondary typo-h6-bold" key={`user-info-title-${memberIndex}`}>
                {t('pages.dashboard.createUser.form.blocks.mainInformation')}
              </p>

              <SummaryInformationCategory
                items={[
                  { label: t('pages.dashboard.createUser.form.title'), value: member.title || '-' },
                  { label: t('pages.dashboard.createUser.form.lastname'), value: member.lastname },
                  {
                    label: t('pages.dashboard.createUser.form.firstname'),
                    value: member.firstname,
                  },
                  { label: t('pages.dashboard.createUser.form.email'), value: member.email },
                ]}
                key={`user-info-${memberIndex}`}
              />

              <Divider thickness="100" key={`section-divider-info-${memberIndex}`} />

              <p className="text-secondary typo-h6-bold" key={`address-title-${memberIndex}`}>
                {t('pages.dashboard.createUser.form.blocks.address')}
              </p>

              <div className="flex gap-8" key={`address-container-${memberIndex}`}>
                <AddressSummary
                  isPrimary
                  title={t('pages.dashboard.createUser.form.mainAddress')}
                  address1={member.address1}
                  address2={member.address2}
                  address3={member.address3}
                  postcode={member.postcode}
                  city={member.city}
                  country={member.country}
                  key={`primary-address-${memberIndex}`}
                />

                <AddressSummary
                  title={t('pages.dashboard.createUser.form.secondaryAddress')}
                  address1={member.secondaryAddress1}
                  address2={member.secondaryAddress2}
                  address3={member.secondaryAddress3}
                  postcode={member.secondaryPostcode}
                  city={member.secondaryCity}
                  country={member.secondaryCountry}
                  key={`secondary-address-${memberIndex}`}
                />
              </div>

              <Divider thickness="100" key={`section-divider-address-${memberIndex}`} />

              <p className="text-secondary typo-h6-bold" key={`contact-title-${memberIndex}`}>
                {t('pages.dashboard.createUser.form.blocks.contact')}
              </p>

              <SummaryInformationCategory
                items={[
                  { label: t('pages.dashboard.createUser.form.phone'), value: member.phone || '-' },
                  { label: t('pages.dashboard.createUser.form.mobile'), value: member.mobile || '-' },
                ]}
                key={`contact-${memberIndex}`}
              />
            </div>
          ),
        },
        {
          label: t('pages.dashboard.createUser.form.blocks.birthNationality'),
          element: (
            <SummaryInformationCategory
              items={[
                { label: t('pages.dashboard.createUser.form.birthday'), value: member.birthday || '-' },
                {
                  label: t('pages.dashboard.createUser.form.birthCountry'),
                  value: member.birthCountry ? t(`shared.countries.${member.birthCountry}`) : '-',
                },
                { label: t('pages.dashboard.createUser.form.placeOfBirth'), value: member.placeOfBirth || '-' },
                {
                  label: t('pages.dashboard.createUser.form.nationalities'),
                  value:
                    [member.nationality, member.nationalityOther]
                      .filter(Boolean)
                      .map((nationality) => t(`shared.countries.${nationality}`))
                      .join(', ') || '-',
                },
              ]}
              key={`birth-nationality-${memberIndex}`}
            />
          ),
        },
        {
          label: t('pages.dashboard.createUser.form.blocks.situationAndTaxResidence'),
          element: (
            <div className="flex flex-col gap-5" key={`situation-and-tax-residence-container-${memberIndex}`}>
              <p className="text-secondary typo-h6-bold" key={`situation-and-tax-residence-title-${memberIndex}`}>
                {t('pages.dashboard.createUser.form.blocks.situation')}
              </p>

              {config?.settings?.users?.dataQuality?.includes('personalSituation') ? (
                <PersonalSituationSummary
                  occupationCategory={member.occupationCategory}
                  monthlyIncomeRange={member.monthlyIncomeRange}
                  personalAssetsRange={member.personalAssetsRange}
                />
              ) : (
                <SummaryInformationCategory
                  items={[
                    {
                      label: t('pages.dashboard.createUser.form.personalAssets.title'),
                      value: member.personalAssets
                        ? t(`pages.dashboard.createUser.form.personalAssets.${member.personalAssets}`)
                        : '-',
                    },
                    {
                      label: t('pages.dashboard.createUser.form.incomeRange.title'),
                      value: member.incomeRange
                        ? t(`pages.dashboard.createUser.form.incomeRange.${member.incomeRange}`)
                        : '-',
                    },
                    {
                      label: t('pages.currentProfiles.profile.financial.occupationType'),
                      value: member.occupationType
                        ? t(`pages.currentProfiles.profile.financial.occupationTypes.${member.occupationType}`)
                        : '-',
                    },
                  ]}
                  key={`situation-and-tax-residence-${memberIndex}`}
                />
              )}

              <Divider thickness="100" key={`section-divider-tax-residence-${memberIndex}`} />

              <p className="text-secondary typo-h6-bold" key={`title-tax-residence-${memberIndex}`}>
                {t('pages.dashboard.createUser.form.blocks.situationAndTaxResidence')}
              </p>

              <SummaryInformationCategory
                items={[
                  {
                    label: t('pages.dashboard.createUser.form.usTaxResident'),
                    value:
                      Number(member.specifiedUSPerson || 0) === 1
                        ? t('translation:components.molecules.forms.yes')
                        : t('translation:components.molecules.forms.no'),
                  },
                ]}
                key={`tax-us-tax-resident-${memberIndex}`}
              />
              <Divider thickness="100" key={`divider-tax-residence-${memberIndex}`} />

              <TaxResidenceSummary taxResidences={member.taxResidences} key={`tax-residence-${memberIndex}`} />
            </div>
          ),
        },
        {
          label: t('pages.dashboard.createUser.form.blocks.documents'),
          element: (
            <div>
              {member.kycDocuments?.length > 0 ? (
                <KycDocumentSummary documents={member.kycDocuments} />
              ) : (
                <p className="typo-body-regular-paragraph text-secondary font-bold">
                  {t('pages.dashboard.createUser.form.pages.indication.noDocument')}
                </p>
              )}
            </div>
          ),
        },
      ] as ITabItem[];
    },
    [config?.settings?.users?.dataQuality, t],
  );

  const memberRows: TAccordionItem[] = useMemo(
    () =>
      user.members?.map((member, memberIndex) => ({
        title: `${member.lastname} ${member.firstname}`,
        description: `(${member.roles.map((role) => t(`pages.dashboard.createUser.form.roles.${role}`).toLocaleLowerCase())?.join(', ')})`,
        children: (
          <Tabs
            items={tabItems(member, memberIndex)}
            size="s"
            onTabChanged={(tabIndex) => {
              const newCurrentTabIndexes = [...currentTabIndexes];
              newCurrentTabIndexes[memberIndex] = tabIndex;
              setCurrentTabIndexes(newCurrentTabIndexes);
            }}
            currentTabIndex={currentTabIndexes[memberIndex]}
          />
        ),
      })),
    [currentTabIndexes, t, tabItems, user.members],
  );

  return <Accordion size="s" accordionContent={memberRows} />;
}
